import React, { useState, useMemo } from "react";
import "./FilterAI.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faMagicWandSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { getAIInfluencerSearchSuggestions } from "services/SauceService";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";

export type FilterAIResponse = {
  minAmountFollowers: number;
  minAmountEngagement: number;
  categories: Array<string>;
  audiences: Array<string>;
  locations: Array<string>;
  gender: Array<string>;
  platform?: string;
  sortField?: "engagement" | "followers";
  sortDirection?: "asc" | "desc";
};

export const buildQuery = (obj: FilterAIResponse): string[] => {
  const params: string[] = [];

  if (obj.platform) {
    params.push(`platform=${encodeURIComponent(obj.platform)}`);
  }
  if (obj.minAmountFollowers !== undefined && obj.minAmountFollowers !== null) {
    params.push(
      `minAmountFollowers=${encodeURIComponent(String(obj.minAmountFollowers))}`
    );
  }

  if (
    obj.minAmountEngagement !== undefined &&
    obj.minAmountEngagement !== null
  ) {
    params.push(
      `minAmountEngagement=${encodeURIComponent(
        String(obj.minAmountEngagement)
      )}`
    );
  }

  if (Array.isArray(obj.categories) && obj.categories.length > 0) {
    const categoriesValue = obj.categories
      .map((cat) => encodeURIComponent(cat))
      .join(";");
    params.push(`categories=${categoriesValue}`);
  }

  const auds: any = obj.audiences;
  let audiencesArray: string[] = [];
  if (Array.isArray(auds) && auds.length > 0) {
    audiencesArray = auds;
  } else if (typeof auds === "string" && auds.trim().length > 0) {
    audiencesArray = auds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (audiencesArray.length > 0) {
    const audiencesValue = audiencesArray
      .map((aud) => encodeURIComponent(aud))
      .join(";");
    params.push(`audiences=${audiencesValue}`);
  }

  if (obj.sortField) {
    params.push(`sortField=${encodeURIComponent(obj.sortField)}`);
  }

  if (obj.sortDirection) {
    params.push(`sortDirection=${encodeURIComponent(obj.sortDirection)}`);
  }

  const locs: any = obj.locations;
  let locationsArray: string[] = [];
  if (Array.isArray(locs) && locs.length > 0) {
    locationsArray = locs;
  } else if (typeof locs === "string" && locs.trim().length > 0) {
    locationsArray = locs
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (locationsArray.length > 0) {
    const locationsValue = locationsArray
      .map((loc) => encodeURIComponent(loc))
      .join(";");
    params.push(`locations=${locationsValue}`);
  }

  const gender: any = obj.gender;
  let genderArray: string[] = [];
  if (Array.isArray(gender) && gender.length > 0) {
    genderArray = gender;
  } else if (typeof gender === "string" && gender.trim().length > 0) {
    genderArray = gender
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (genderArray.length > 0) {
    const genderValue = genderArray.map((g) => encodeURIComponent(g)).join(";");
    params.push(`gender=${genderValue}`);
  }

  return params;
};

const placeholders = [
  "Find fitness and wellness creators in Los Angeles with 10K-100K followers targeting women 20-35.",
  "Show food and coffee influencers in New York City with 5K-50K Instagram followers and a mostly local audience.",
  "Find travel and adventure creators in Europe with 20K+ followers whose audience is mainly US travelers.",
  "Show DIY home-renovation creators in Toronto with micro-influencer reach (3K-25K followers).",
  "Find mom blogger influencers in Texas with high engagement and an audience of parents with kids under 10.",
  "Show beauty and makeup creators in Miami with 15K-80K followers targeting women 18-30.",
  "Find streetwear fashion influencers in London with 10K-60K followers and a Gen Z audience.",
  "Show skincare and wellness creators in the US with 20K+ followers and a female audience 25-44.",
  "Find gaming and tech creators on TikTok in the UK with 25K-250K followers.",
  "Show comedy and entertainment influencers in Chicago with 5K-40K followers and high Reels views.",
  "Find music and DJ creators in Berlin with 10K-100K followers who post nightlife content.",
  "Show pet influencers (dogs and cats) in LA with 5K-30K followers and a mostly US audience.",
  "Find interior design and home décor creators in Canada with 10K-70K followers.",
  "Show business and entrepreneurship influencers in the US with 20K+ followers who talk about small business growth.",
  "Find sustainability and climate-activism creators in Europe with 15K-150K followers and a young adult audience.",
  "Show science and education creators who explain physics and chemistry with 5K-50K followers.",
  "Find college study-tip influencers in the US with 10K-80K followers and a primarily student audience.",
  "Show art and illustration creators (digital or traditional) in North America with 5K-60K followers.",
  "Find sports creators focused on team sports in New York with 8K-70K followers.",
  "Show shopping and retail deal influencers in the US with 20K-200K followers who post daily offers.",
];

const FilterAI: React.FC = () => {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const randomPlaceholder = useMemo(
    () =>
      `Try: ${placeholders[Math.floor(Math.random() * placeholders.length)]}`,
    []
  );

  const onSubmit = async () => {
    if (!input.trim() || loading) return;

    try {
      setLoading(true);
      const response: any = await getAIInfluencerSearchSuggestions({
        query: input,
      });
      if (response.success) {
        const queryString = buildQuery(response.data).join("&");
        navigate(`/creators?${queryString}`);
      }
    } catch (error) {
      console.error("Error fetching AI suggestions:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="filter-ai-input animate-pulse-glow">
      <span className="filter-ai-input__icon">
        <FontAwesomeIcon icon={faMagicWandSparkles} /> AI
      </span>
      <input
        type="text"
        className="filter-ai-input__input"
        placeholder={randomPlaceholder}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !loading) {
            onSubmit();
          }
        }}
        disabled={loading}
      />
      <button
        className="filter-ai-input__button"
        onClick={onSubmit}
        type="button"
        disabled={loading}
      >
        {loading ? (
          <Spin size="small" />
        ) : (
          <FontAwesomeIcon icon={faArrowRight} />
        )}
      </button>
    </div>
  );
};

export default FilterAI;
